/* ============================================================================
 * Onboarding state (localStorage, per user). Powers the Welcome modal, the
 * guided tour, and the Getting-Started checklist. No backend — progress is
 * per-browser, which is fine for orientation. A small event bus lets any
 * component trigger the tour / welcome and react to progress changes.
 * ==========================================================================*/

export type OnboardingStepId =
  | 'search_sam'
  | 'open_vendor'
  | 'price_benchmark'
  | 'create_opportunity'
  | 'run_estimate'
  | 'complete_intake';

export interface OnboardingStepDef {
  id: OnboardingStepId;
  label: string;
  hint: string;
  path: string; // where to go to do it
}

// The getting-started steps, in the order a new procurement/CO user would do them.
export const ONBOARDING_STEPS: OnboardingStepDef[] = [
  { id: 'search_sam', label: 'Search the SAM.gov Library', hint: 'Find a federal notice or award by keyword, NAICS, or agency.', path: '/sam-library' },
  { id: 'open_vendor', label: 'Open a vendor profile', hint: 'Review a vendor’s capabilities, past awards, and subcontracting history.', path: '/vendors' },
  { id: 'price_benchmark', label: 'Run a Price Benchmark', hint: 'See what agencies paid for a NAICS/PSC and get an AI cost estimate.', path: '/price-benchmark' },
  { id: 'create_opportunity', label: 'Create an Opportunity', hint: 'Start tracking a procurement through its lifecycle.', path: '/opportunities' },
  { id: 'run_estimate', label: 'Generate an AI cost estimate', hint: 'Describe the work to get a grounded IGCE range.', path: '/opportunities' },
  { id: 'complete_intake', label: 'Complete Intake (FAR Part 11)', hint: 'Define the requirement: mission need + PWS/SOW or description.', path: '/opportunities' },
];

const EVT = 'fti-onboarding-changed';

function userKey(): string {
  try {
    // Scope keys to the signed-in user when we can infer it from the DOM/localStorage.
    // Falls back to a shared key; good enough for per-browser orientation.
    const u = localStorage.getItem('fti_current_user') || 'user';
    return u.toLowerCase();
  } catch { return 'user'; }
}

export function setCurrentUser(email: string) {
  try { if (email) localStorage.setItem('fti_current_user', email.toLowerCase()); } catch { /* noop */ }
}

function k(suffix: string): string {
  return `fti_onboarding_${userKey()}_${suffix}`;
}

function emitChange() {
  try { window.dispatchEvent(new CustomEvent(EVT)); } catch { /* noop */ }
}

export function onOnboardingChange(cb: () => void): () => void {
  window.addEventListener(EVT, cb);
  return () => window.removeEventListener(EVT, cb);
}

// ── First-run / welcome ──
export function isFirstRun(): boolean {
  try { return localStorage.getItem(k('welcomed')) !== '1'; } catch { return false; }
}
export function markWelcomed() {
  try { localStorage.setItem(k('welcomed'), '1'); } catch { /* noop */ }
}
export function resetWelcome() {
  try { localStorage.removeItem(k('welcomed')); } catch { /* noop */ }
  emitChange();
}

// ── Tour ──
export function tourCompleted(): boolean {
  try { return localStorage.getItem(k('tour')) === '1'; } catch { return false; }
}
export function markTourCompleted() {
  try { localStorage.setItem(k('tour'), '1'); } catch { /* noop */ }
  emitChange();
}
/** Trigger the guided tour from anywhere (e.g. the "?" menu or Welcome modal). */
export function startTour() {
  try { window.dispatchEvent(new CustomEvent('fti-start-tour')); } catch { /* noop */ }
}
export function onStartTour(cb: () => void): () => void {
  window.addEventListener('fti-start-tour', cb);
  return () => window.removeEventListener('fti-start-tour', cb);
}
/** Trigger the welcome modal (replay from "?" menu). */
export function openWelcome() {
  try { window.dispatchEvent(new CustomEvent('fti-open-welcome')); } catch { /* noop */ }
}
export function onOpenWelcome(cb: () => void): () => void {
  window.addEventListener('fti-open-welcome', cb);
  return () => window.removeEventListener('fti-open-welcome', cb);
}

// ── Checklist steps ──
export function isStepDone(id: OnboardingStepId): boolean {
  try { return localStorage.getItem(k(`step_${id}`)) === '1'; } catch { return false; }
}
export function markOnboardingStep(id: OnboardingStepId) {
  try {
    if (localStorage.getItem(k(`step_${id}`)) === '1') return; // already done
    localStorage.setItem(k(`step_${id}`), '1');
  } catch { /* noop */ }
  emitChange();
}
export function getStepStates(): Record<OnboardingStepId, boolean> {
  const out = {} as Record<OnboardingStepId, boolean>;
  ONBOARDING_STEPS.forEach(s => { out[s.id] = isStepDone(s.id); });
  return out;
}
export function completedCount(): number {
  return ONBOARDING_STEPS.filter(s => isStepDone(s.id)).length;
}

// ── Checklist widget dismissal ──
export function checklistDismissed(): boolean {
  try { return localStorage.getItem(k('checklist_dismissed')) === '1'; } catch { return false; }
}
export function dismissChecklist() {
  try { localStorage.setItem(k('checklist_dismissed'), '1'); } catch { /* noop */ }
  emitChange();
}
export function restoreChecklist() {
  try { localStorage.removeItem(k('checklist_dismissed')); } catch { /* noop */ }
  emitChange();
}
